<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="pageContainer">
    <ReportsHomeV2
      title="Export Monitoring Log"
      placeholder="Job No"
      :jsReport="false"
      :designerV3="true"
      :inputValue.sync="jobNo"
      @generateReport="generateReport"
      :designerV3ReportFiles="designerV3ReportFiles"
      :designerV3ReportData="designerV3ReportData"
      ref="mainReport"
    />
    <!--Fullscreenloading does not cover top bar, hence Loader is used-->
    <Loader :isVisible.sync="isLoading" :loadingText="loadingText" :isFullScreen="true" />
  </div>
</template>

<script>
import ReportsHomeV2 from '@/pages/ReportsV2/ReportsHome';
export default {
  components: {
    ReportsHomeV2,
  },
  data: function () {
    return {
      isLoading: false,
      loadingText: '',
      transModeList: [],
      jobNo: '',
      designerV3ReportFiles: [],
      designerV3ReportData: [],
    };
  },
  props: {},

  methods: {
    formatDateTime: function (dateTime, format) {
      if (!dateTime) return;
      return new Date(dateTime).formatString(format);
    },
    getJobDetails: async function () {
      const loginDetails = await this.$apiCallV2('/Login/GetDetails');
      const jobDetails = await this.$apiCallV2(
        `/Report/GetJobDetails?config=${loginDetails.ConfigCode}&userID=${loginDetails.LoginUserId}&jobno=${this.jobNo}&rpt=RptJobContainerAttach`,
      );
      
      if (jobDetails.length == 0) {
        await this.$showAlert('error', 'Job Not Found', `No Job with Job No. '${this.jobNo}' found.`, [
        ]);
        return false;
      }

      //Show Header Image
      const headerImage = loginDetails.CoHeaderOpr;
      jobDetails.JobDetails.Job['_header'] = headerImage;
      jobDetails.JobDetails.Job['_showCompanyLetterHead'] = true;
      jobDetails.JobDetails.Job['companyName'] = loginDetails.CoName;
      const preferredDateFormat = this.$store.state['preferredDateFormat'];
      jobDetails.JobDetails.Job['Job Time'] = this.formatDateTime(
        jobDetails.JobDetails.Job['Job Time'],
        `${preferredDateFormat} HH:mm`,
      );
      jobDetails.JobDetails.Job['Closing Time'] = this.formatDateTime(
        jobDetails.JobDetails.Job['Closing Time'],
        `${preferredDateFormat} HH:mm`,
      );
      jobDetails.JobDetails.Job['POL ETA'] = this.formatDateTime(
        jobDetails.JobDetails.Job['POL ETA'],
        `${preferredDateFormat} HH:mm`,
      );

      if (jobDetails.JobDetails.Job['Documents']) {
        for (const doc of jobDetails.JobDetails.Job['Documents'])
          doc['Doc Rec Date'] = this.formatDateTime(doc['Doc Rec Date'], preferredDateFormat); // date only, no time
      }

      jobDetails.JobDetails.Job['timePrinted'] = this.formatDateTime(new Date(), 'yyyy-MM-dd HH:mm:ss');

      if (jobDetails.JobDetails.Job['ContDetail']) {
        const showRF = jobDetails.JobDetails.Job['ContDetail'].filter(
          (c) => c['Cont Type'] == 'RF' || c['Cont Type'] == 'HRF',
        );
        jobDetails.JobDetails.Job['showRF'] = showRF.length > 0;
      }

      jobDetails.JobDetails.Job['shipCallNoLabel'] = this.getPlaceholders(
        jobDetails.JobDetails.Job['Trans Mode'],
        'ShipCallNo',
      );
      jobDetails.JobDetails.Job['vesselIDLabel'] = this.getPlaceholders(
        jobDetails.JobDetails.Job['Trans Mode'],
        'VesselID',
      );
      jobDetails.JobDetails.Job['vesselNameLabel'] = this.getPlaceholders(
        jobDetails.JobDetails.Job['Trans Mode'],
        'VesselName',
      );
      jobDetails.JobDetails.Job['shippingAgentLabel'] = this.getPlaceholders(
        jobDetails.JobDetails.Job['Trans Mode'],
        'ShippingAgent',
      );

      // Format Shipper Address
      jobDetails.JobDetails.Job['_shipperAddress'] = [
        jobDetails.JobDetails.Job['Shipper Add1'],
        jobDetails.JobDetails.Job['Shipper Add2'],
        jobDetails.JobDetails.Job['Shipper Add3'],
        jobDetails.JobDetails.Job['Shipper Add4'],
        `${jobDetails.JobDetails.Job['Shipper PostCode']} ${jobDetails.JobDetails.Job['Shipper City']}`.trim(),
        `${jobDetails.JobDetails.Job['Shipper State']} ${jobDetails.JobDetails.Job['Shipper Country']}`.trim(),
      ]
        .filter(Boolean)
        .join('\n');

      // Format Consignee Address
      jobDetails.JobDetails.Job['_consigneeAddress'] = [
        jobDetails.JobDetails.Job['Consignee Add1'],
        jobDetails.JobDetails.Job['Consignee Add2'],
        jobDetails.JobDetails.Job['Consignee Add3'],
        jobDetails.JobDetails.Job['Consignee Add4'],
        `${jobDetails.JobDetails.Job['Consignee PostCode']} ${jobDetails.JobDetails.Job['Consignee City']}`.trim(),
        `${jobDetails.JobDetails.Job['Consignee State']} ${jobDetails.JobDetails.Job['Consignee Country']}`.trim(),
      ]
        .filter(Boolean)
        .join('\n');

      if (jobDetails.JobDetails.Job['Volume']) {
        jobDetails.JobDetails.Job['Volume'] = this.$volumeFormat(jobDetails.JobDetails.Job['Volume']);
      }

      //Increasing index for Container no.
      jobDetails.JobDetails.Job['ContDetail'].forEach((container, index) => {
        container['NO'] = index + 1;
      });

      const qrData = JSON.stringify({ JOBNO: this.jobNo });
      jobDetails.JobDetails.Job['_QRImage'] = await this.$getQRURL(qrData, { size: 150, margin: 10 });
      
      this.designerV3ReportData = [jobDetails.JobDetails.Job];
      return true;

    },

    getPlaceholders: function (transmode, transmodeKey) {
      if (transmode) {
        const selectedTransMode = this.transModeList.find((t) => t['Trans Mode'] == transmode);
        if (selectedTransMode) {
          const placeholder = selectedTransMode[transmodeKey];
          if (placeholder) {
            return placeholder;
          }
        }
      }
    },

    generateReport: async function () {
      this.isLoading = true;
      this.loadingText = 'Getting Job Details';
      if (await this.getJobDetails()) {
        this.designerV3ReportFiles = ['ExportMonitoringLog.json'];
      }
    },

    updateProgress: function(mountedPromises, promisesFulfilled) {
      const progress = Math.round((promisesFulfilled / mountedPromises.length) * 100);
      this.loadingText = `Loading... ${progress}%`;
    }
  },

  async mounted() {
    const jobNo = this.$route.query.jobno;
    if (jobNo) {
      this.jobNo = jobNo;
      await this.generateReport();
    }
    const mountedPromises = [];
    this.isLoading = true;
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        promisesFulfilled++;
        this.userDetails = result;
        this.updateProgress(mountedPromises, promisesFulfilled); 
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'TRANSMODE' }).then((result) => {
        promisesFulfilled++;
        this.transportModeListMaster = result;
        this.updateProgress(mountedPromises, promisesFulfilled);
      }),
    );
    await Promise.allSettled(mountedPromises);
    this.isLoading = false;
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.pageContainer {
  height: 100%;
  width: 100%;
}
</style>